// A loop preview with no seam. The preview streams (audio/audition.js) play a
// lossy transcode through one <audio loop>: AAC and Opus carry encoder
// padding, and the element restarts at each wrap, so every pass ends in a
// gap (~45 ms on Safari's AAC). This decodes the loop's 16-bit WAV (no
// padding; /download/stream/<hash>?fmt=wav) and loops it on the Web Audio
// clock, which repeats sample-exactly, as the jam engine does for Play all.
//
// The element keeps playing while the WAV loads, so a preview still starts
// at once. Once it's decoded, the buffer takes over at the element's next
// wrap (takeOverAt), and from then on owns playback: start, pause, seek and
// the phase the progress ring shows.
//
// I/O comes in (context, fetchArrayBuffer) so tests can drive it.
//   context           an AudioContext (or a fake)
//   fetchArrayBuffer  (url, { signal }) => Promise<ArrayBuffer>

// A switch closer than this to the element's wrap waits one more pass, so the
// source is never scheduled in the past.
export const MIN_LEAD_SECONDS = 0.05;
// Longer loops keep the stream: a minute of 16-bit stereo 48 kHz WAV is
// ~11.5 MB to download on top of the preview.
export const MAX_GAPLESS_SECONDS = 60;

export class GaplessLoop {
  constructor({ context, fetchArrayBuffer }) {
    this.context = context;
    this.fetchArrayBuffer = fetchArrayBuffer;
    this.buffer = null;
    this.source = null;
    this.startedAt = 0; // context time of the loop's phase 0
    this.pausedAt = 0; // seconds into the loop when paused
    this.playing = false;
    this.loop = true;
    this.onEnded = null; // a one-pass play reached its end
    this._abort = null;
  }

  get duration() {
    return this.buffer ? this.buffer.duration : 0;
  }

  get ready() {
    return !!this.buffer && this.duration > 0;
  }

  /** Fetch and decode `url`. Resolves true when ready, false if cancelled
   * or it failed (the stream keeps playing either way). */
  async load(url) {
    this.cancel();
    const abort =
      typeof AbortController === "function" ? new AbortController() : null;
    this._abort = abort;
    try {
      const data = await this.fetchArrayBuffer(url, {
        signal: abort ? abort.signal : undefined,
      });
      if (this._abort !== abort) return false;
      const buffer = await decode(this.context, data);
      if (this._abort !== abort || !buffer || !(buffer.duration > 0))
        return false;
      this.buffer = buffer;
      return true;
    } catch (e) {
      return false;
    } finally {
      if (this._abort === abort) this._abort = null;
    }
  }

  /** Abandon a load in flight. */
  cancel() {
    if (this._abort) {
      try {
        this._abort.abort();
      } catch (e) {
        /* already settled */
      }
    }
    this._abort = null;
  }

  /** Take over from a stream `elementTime` seconds into its playback, at its
   * next wrap. Returns the seconds until the switch (when the caller should
   * silence the element). */
  takeOverAt(elementTime) {
    const d = this.duration;
    const into = ((elementTime % d) + d) % d;
    let remaining = d - into;
    if (remaining < MIN_LEAD_SECONDS) remaining += d;
    const when = this.context.currentTime + remaining;
    this._startSource(when, 0);
    this.startedAt = when;
    this.playing = true;
    return remaining;
  }

  /** Play now from `offset` seconds into the loop. */
  play(offset = 0) {
    const d = this.duration;
    const at = d > 0 ? ((offset % d) + d) % d : 0;
    const now = this.context.currentTime;
    this._startSource(now, at);
    this.startedAt = now - at;
    this.playing = true;
  }

  /** Pause, remembering where. */
  pause() {
    if (!this.playing) return this.pausedAt;
    this.pausedAt = this.position();
    this._stopSource();
    this.playing = false;
    return this.pausedAt;
  }

  /** Jump to `frac` (0..1) of the loop, playing or not. */
  seek(frac) {
    const at = Math.max(0, Math.min(1, frac)) * this.duration;
    if (this.playing) this.play(at);
    else this.pausedAt = at;
  }

  /** Loop or play once more to the end (the bar's one-pass mode). */
  setLoop(loop) {
    this.loop = !!loop;
    if (this.source) this.source.loop = this.loop;
  }

  /** Seconds into the loop now (0 before a scheduled takeover starts). */
  position() {
    if (!this.playing) return this.pausedAt;
    const d = this.duration;
    const t = this.context.currentTime - this.startedAt;
    if (d <= 0 || t <= 0) return 0;
    return this.loop ? t % d : Math.min(t, d);
  }

  /** 0..1 of the loop played. */
  phase() {
    const d = this.duration;
    return d > 0 ? this.position() / d : 0;
  }

  /** Stop and drop the buffer. */
  release() {
    this.cancel();
    this._stopSource();
    this.playing = false;
    this.pausedAt = 0;
    this.buffer = null;
  }

  _startSource(when, offset) {
    this._stopSource();
    const src = this.context.createBufferSource();
    src.buffer = this.buffer;
    src.loop = this.loop;
    src.connect(this.context.destination);
    src.onended = () => {
      // A stop() we made, or a source that was replaced: not an end.
      if (this.source !== src) return;
      this.source = null;
      this.playing = false;
      this.pausedAt = 0;
      if (typeof this.onEnded === "function") this.onEnded();
    };
    src.start(when, offset);
    this.source = src;
  }

  _stopSource() {
    const src = this.source;
    this.source = null;
    if (!src) return;
    try {
      src.stop();
    } catch (e) {
      /* never started */
    }
    try {
      src.disconnect();
    } catch (e) {
      /* not connected */
    }
  }
}

// decodeAudioData: Safari before 14.1 has only the callback form.
function decode(context, data) {
  return new Promise((resolve, reject) => {
    const p = context.decodeAudioData(data, resolve, reject);
    if (p && typeof p.then === "function") p.then(resolve, reject);
  });
}
